/**
 * Platform-admin mock data.
 * Shapes mirror NESTJS_SCHEMA.md tables: resellers, audit_logs, webhook_deliveries,
 * payments, system_health (derived).
 */
import type { Payment } from '../contracts/schemas';

export type Reseller = {
  id: string;
  name: string;
  slug: string;
  ownerEmail: string;
  ownerName: string;
  primaryColor: string;
  businessCount: number;
  mrrMinor: number;
  commissionPct: number;
  status: 'active' | 'invited' | 'frozen' | 'terminated';
  createdAt: string;
  pricingMode: 'commission' | 'markup' | 'own_packages';
};

export const resellers: Reseller[] = [
  {
    id: 'rsl_1',
    name: 'Acme Resellers',
    slug: 'acme',
    ownerEmail: 'rakib@acme-resellers.com',
    ownerName: 'Rakib Ahmed',
    primaryColor: '#0F766E',
    businessCount: 128,
    mrrMinor: 19240000,
    commissionPct: 25,
    status: 'active',
    createdAt: '2026-01-12',
    pricingMode: 'own_packages',
  },
  {
    id: 'rsl_2',
    name: 'Sundarban Tech',
    slug: 'sundarbn',
    ownerEmail: 'admin@sundarbantech.com',
    ownerName: 'Mahfuz Rahman',
    primaryColor: '#0369A1',
    businessCount: 73,
    mrrMinor: 9800000,
    commissionPct: 10,
    status: 'active',
    createdAt: '2025-11-04',
    pricingMode: 'own_packages',
  },
  {
    id: 'rsl_3',
    name: 'CityPOS Bangla',
    slug: 'citypos',
    ownerEmail: 'contact@citypos.bd',
    ownerName: 'Nurul Haque',
    primaryColor: '#9333EA',
    businessCount: 51,
    mrrMinor: 7400000,
    commissionPct: 5,
    status: 'active',
    createdAt: '2025-09-22',
    pricingMode: 'own_packages',
  },
  {
    id: 'rsl_4',
    name: 'Beta Resellers',
    slug: 'beta-res',
    ownerEmail: 'hello@beta-res.com',
    ownerName: 'Tanvir Ahmed',
    primaryColor: '#4F46E5',
    businessCount: 0,
    mrrMinor: 0,
    commissionPct: 15,
    status: 'invited',
    createdAt: '2026-04-25',
    pricingMode: 'own_packages',
  },
  {
    id: 'rsl_5',
    name: 'Gone Reseller',
    slug: 'gone',
    ownerEmail: 'closed@gone.com',
    ownerName: 'Closed Account',
    primaryColor: '#525B6B',
    businessCount: 12,
    mrrMinor: 0,
    commissionPct: 20,
    status: 'frozen',
    createdAt: '2025-06-01',
    pricingMode: 'commission',
  },
];

export const platformKpis = {
  resellers: 12,
  businesses: 521,
  users: 487,
  mrrMinor: 74000000,
  uptimePct: 99.94,
};

export const platformActivity: Array<{
  id: string;
  at: string;
  event: string;
  detail: string;
  status?: 'ok' | 'warn' | 'error';
}> = [
  { id: 'pa1', at: '2026-05-03T14:32:00Z', event: 'New signup', detail: 'Greenfields Mart (Pro · via Acme Res.)', status: 'ok' },
  { id: 'pa2', at: '2026-05-03T14:28:00Z', event: 'Payment success', detail: '৳1500 INV-2026-0342', status: 'ok' },
  { id: 'pa3', at: '2026-05-03T14:21:00Z', event: 'Business suspended', detail: 'Khan Bakery (past due 7d)', status: 'warn' },
  { id: 'pa4', at: '2026-05-03T14:11:00Z', event: 'Provisioning OK', detail: 'business_db_198 (32s)', status: 'ok' },
  { id: 'pa5', at: '2026-05-03T14:02:00Z', event: 'Webhook retry', detail: 'business.status_changed (success)', status: 'ok' },
  { id: 'pa6', at: '2026-05-03T13:51:00Z', event: 'Cash entry', detail: '৳300 by sayem@…', status: 'ok' },
  { id: 'pa7', at: '2026-05-03T13:42:00Z', event: 'Coupon redeemed', detail: 'EID2026 → Sky Pharmacy', status: 'ok' },
];

export const businessesByStatus = [
  { status: 'active' as const, count: 412 },
  { status: 'trial' as const, count: 54 },
  { status: 'past_due' as const, count: 32 },
  { status: 'suspended' as const, count: 18 },
  { status: 'canceled' as const, count: 5 },
];

export const topResellers: Array<{ id: string; name: string; mrrMinor: number }> = [
  { id: 'rsl_1', name: 'Acme Resellers', mrrMinor: 19240000 },
  { id: 'rsl_2', name: 'Sundarban Tech', mrrMinor: 9800000 },
  { id: 'rsl_3', name: 'CityPOS Bangla', mrrMinor: 7400000 },
];

/**
 * Businesses list (admin-scoped). Fuller shape than the owner-side `Business`.
 */
export type AdminBusiness = {
  id: string;
  name: string;
  resellerId: string | null;
  resellerName: string | null;
  ownerEmail: string;
  plan: string;
  status: 'active' | 'trial' | 'past_due' | 'suspended' | 'canceled' | 'pending_payment';
  dbMode: 'managed' | 'self_hosted' | 'legacy';
  createdAt: string;
  /** Set when a chargeback flagged the business for manual payment review (MONEY_OUT_SPEC §6). */
  paymentReviewAt?: string | null;
  mrrMinor: number;
};

export const adminBusinesses: AdminBusiness[] = [
  { id: 'biz_42', name: 'Khan Bakery', resellerId: null, resellerName: null, ownerEmail: 'imran@khan-bakery.com', plan: 'Pro', status: 'active', dbMode: 'legacy', createdAt: '2025-04-25', mrrMinor: 150000 },
  { id: 'biz_300', name: 'Acme Main Store', resellerId: 'rsl_1', resellerName: 'Acme Resellers', ownerEmail: 'sayem@acme.com', plan: 'Pro', status: 'active', dbMode: 'managed', createdAt: '2026-01-15', mrrMinor: 150000 },
  { id: 'biz_301', name: 'Sky Pharmacy', resellerId: 'rsl_2', resellerName: 'Sundarban Tech', ownerEmail: 'sky@sky-pharma.bd', plan: 'Basic', status: 'past_due', dbMode: 'managed', createdAt: '2026-02-04', mrrMinor: 50000 },
  { id: 'biz_181', name: 'Nadia Boutique', resellerId: null, resellerName: null, ownerEmail: 'nadia@boutique.com', plan: 'Pro', status: 'active', dbMode: 'self_hosted', createdAt: '2026-03-15', mrrMinor: 150000 },
  { id: 'biz_198', name: 'Greenfields Mart', resellerId: 'rsl_1', resellerName: 'Acme Resellers', ownerEmail: 'greenfields@mart.com', plan: 'Pro', status: 'trial', dbMode: 'managed', createdAt: '2026-05-03', mrrMinor: 150000 },
  { id: 'biz_201', name: 'Demo Store', resellerId: null, resellerName: null, ownerEmail: 'demo@itcitysoft.com', plan: 'Basic', status: 'trial', dbMode: 'managed', createdAt: '2026-04-30', mrrMinor: 0 },
];

/**
 * Webhook deliveries (last 24 h).
 */
export type WebhookDelivery = {
  id: string;
  at: string;
  event: 'business.status_changed' | 'business.provisioned' | 'payment.received' | 'payment.failed';
  target: string;
  status: number; // HTTP status
  attempt: number;
  isRetry: boolean;
};

export const webhookDeliveries: WebhookDelivery[] = [
  { id: 'wh1', at: '2026-05-03T14:32:00Z', event: 'business.status_changed', target: 'pos.legacy', status: 200, attempt: 1, isRetry: false },
  { id: 'wh2', at: '2026-05-03T14:28:00Z', event: 'business.provisioned', target: 'pos.legacy', status: 200, attempt: 1, isRetry: false },
  { id: 'wh3', at: '2026-05-03T14:11:00Z', event: 'payment.received', target: 'pos.legacy', status: 503, attempt: 1, isRetry: false },
  { id: 'wh4', at: '2026-05-03T14:11:00Z', event: 'payment.received', target: 'pos.legacy', status: 200, attempt: 2, isRetry: true },
  { id: 'wh5', at: '2026-05-03T13:42:00Z', event: 'business.status_changed', target: 'pos.legacy', status: 200, attempt: 1, isRetry: false },
  { id: 'wh6', at: '2026-05-03T12:14:00Z', event: 'payment.received', target: 'pos.legacy', status: 200, attempt: 1, isRetry: false },
];

/**
 * System health (derived from infrastructure metrics).
 */
export const systemHealth = {
  services: [
    { name: 'PostgreSQL', status: 'ok' as const },
    { name: 'Redis', status: 'ok' as const },
    { name: 'MariaDB primary', status: 'ok' as const },
    { name: 'MariaDB replica', status: 'ok' as const },
    { name: 'NestJS API', status: 'ok' as const },
    { name: 'BullMQ workers', status: 'ok' as const },
    { name: 'Laravel POS', status: 'ok' as const },
  ],
  queueDepth: { invoices: 0, notifications: 3, provisioning: 1, webhooks: 0 },
  replicaLagSec: 0.4,
  replicaLagThresholdSec: 5,
  databaseDistribution: { managed: 387, selfHost: 18, legacy: 116 },
  recentIncidents: [
    { id: 'inc1', at: '02 May 04:14', summary: 'Replica lag spiked to 18s for 4 min', resolution: 'resolved' as const },
    { id: 'inc2', at: '28 Apr 22:00', summary: 'SSLCommerz IPN delayed 12 min', resolution: 'acknowledged' as const },
  ],
};

/**
 * Manual cash payment search-result candidates.
 */
export const cashEntryCandidate = {
  business: {
    id: 'biz_42',
    name: 'Khan Bakery',
    plan: 'Starter',
    status: 'past_due' as const,
    pastDueSince: '2026-04-25',
    owedMinor: 30000,
  },
};

/**
 * Payment gateways catalog (NOT a DB table — derived/static).
 */
export const paymentGateways: Array<{
  code: 'sslcommerz' | 'manual_cash' | 'stripe' | 'paypal';
  label: string;
  mode: 'live' | 'sandbox' | 'disabled' | '—';
  lastVerified: string | null;
  isDefault: boolean;
}> = [
  { code: 'sslcommerz', label: 'SSLCommerz', mode: 'live', lastVerified: '03 May 26 14:00', isDefault: true },
  { code: 'manual_cash', label: 'Cash (manual)', mode: '—', lastVerified: 'always', isDefault: false },
  { code: 'stripe', label: 'Stripe (legacy)', mode: 'disabled', lastVerified: null, isDefault: false },
  { code: 'paypal', label: 'PayPal (legacy)', mode: 'disabled', lastVerified: null, isDefault: false },
];

/**
 * Cross-org users (platform admin view). Each user has memberships into
 * resellers and/or businesses; `memberships` mirrors the `business_users` +
 * `reseller_users` pivots in NESTJS_SCHEMA.
 */
export type AdminUser = {
  id: string;
  email: string;
  username?: string | null;
  name: string;
  primaryRole: 'platform_admin' | 'reseller_admin' | 'business_admin' | 'business_staff';
  primaryScope: string;
  status: 'active' | 'invited' | 'disabled';
  lastLogin: string | null;
  createdAt: string;
  mfaEnabled: boolean;
  emailVerifiedAt: string | null;
  phoneVerifiedAt: string | null;
  contactPhone: string | null;
  language: 'en' | 'bn';
  memberships: Array<{
    type: 'reseller' | 'business';
    id: string;
    name: string;
    role: string;
    joinedAt: string;
  }>;
  recentSessions: Array<{
    id: string;
    device: string;
    location: string;
    ip: string;
    lastActive: string;
    current: boolean;
  }>;
};

export const adminUsers: AdminUser[] = [
  {
    id: 'u1',
    email: 'sayem@rootsoftit.com',
    name: 'Sayem Khan',
    primaryRole: 'platform_admin',
    primaryScope: 'ITCity',
    status: 'active',
    lastLogin: 'today 14:01',
    createdAt: '2025-09-01',
    mfaEnabled: true,
    emailVerifiedAt: '2025-09-01',
    phoneVerifiedAt: '2025-09-01',
    contactPhone: '+8801717540035',
    language: 'en',
    memberships: [],
    recentSessions: [
      { id: 'ses1', device: 'MacBook · Chrome 132', location: 'Sylhet, BD', ip: '202.74.X.X', lastActive: 'now', current: true },
      { id: 'ses2', device: 'iPhone · Safari', location: 'Sylhet, BD', ip: '180.211.X.X', lastActive: '4 hours ago', current: false },
    ],
  },
  {
    id: 'u2',
    email: 'rakib@acme-resellers.com',
    name: 'Rakib Ahmed',
    primaryRole: 'reseller_admin',
    primaryScope: 'Acme Resellers',
    status: 'active',
    lastLogin: 'today 09:14',
    createdAt: '2026-01-12',
    mfaEnabled: true,
    emailVerifiedAt: '2026-01-12',
    phoneVerifiedAt: '2026-01-13',
    contactPhone: '+8801XXXXXXX',
    language: 'en',
    memberships: [
      { type: 'reseller', id: 'rsl_1', name: 'Acme Resellers', role: 'admin', joinedAt: '2026-01-12' },
    ],
    recentSessions: [
      { id: 'ses3', device: 'Windows · Edge', location: 'Sylhet, BD', ip: '103.85.X.X', lastActive: '2 hours ago', current: true },
    ],
  },
  {
    id: 'u3',
    email: 'samira@acme-resellers.com',
    name: 'Samira Khatun',
    primaryRole: 'reseller_admin',
    primaryScope: 'Acme Resellers',
    status: 'active',
    lastLogin: 'yesterday',
    createdAt: '2026-02-04',
    mfaEnabled: false,
    emailVerifiedAt: '2026-02-04',
    phoneVerifiedAt: null,
    contactPhone: '+8801XXXXXXX',
    language: 'bn',
    memberships: [
      { type: 'reseller', id: 'rsl_1', name: 'Acme Resellers', role: 'staff', joinedAt: '2026-02-04' },
    ],
    recentSessions: [],
  },
  {
    id: 'u4',
    email: 'sayem@acme.com',
    name: 'Sayem (Acme)',
    primaryRole: 'business_admin',
    primaryScope: 'Acme Main Store',
    status: 'active',
    lastLogin: 'today 11:20',
    createdAt: '2026-01-15',
    mfaEnabled: true,
    emailVerifiedAt: '2026-01-15',
    phoneVerifiedAt: '2026-01-15',
    contactPhone: '+8801XXXXXXX',
    language: 'en',
    memberships: [
      { type: 'business', id: 'biz_300', name: 'Acme Main Store', role: 'admin', joinedAt: '2026-01-15' },
    ],
    recentSessions: [
      { id: 'ses4', device: 'iPad · Safari', location: 'Dhaka, BD', ip: '203.190.X.X', lastActive: '30 min ago', current: false },
    ],
  },
  {
    id: 'usr_10',
    email: 'imran@khan-bakery.com',
    name: 'Imran Khan',
    primaryRole: 'business_admin',
    primaryScope: 'Khan Bakery',
    status: 'active',
    lastLogin: '02 May',
    createdAt: '2025-04-25',
    mfaEnabled: true,
    emailVerifiedAt: '2025-04-25',
    phoneVerifiedAt: '2025-04-25',
    contactPhone: '+8801717540035',
    language: 'bn',
    memberships: [
      { type: 'business', id: 'biz_42', name: 'Khan Bakery', role: 'admin', joinedAt: '2025-04-25' },
      { type: 'business', id: 'biz_43', name: 'Khan Bakery — Dhaka', role: 'admin', joinedAt: '2026-04-12' },
      { type: 'business', id: 'biz_44', name: 'Khan Catering', role: 'admin', joinedAt: '2025-12-01' },
    ],
    recentSessions: [
      { id: 'ses5', device: 'MacBook · Chrome', location: 'Sylhet, BD', ip: '202.74.X.X', lastActive: 'now', current: true },
      { id: 'ses6', device: 'iPhone · Safari', location: 'Sylhet, BD', ip: '180.211.X.X', lastActive: '4 hours ago', current: false },
      { id: 'ses7', device: 'Windows · Edge', location: 'Dhaka, BD', ip: '103.85.X.X', lastActive: '2 days ago', current: false },
    ],
  },
  {
    id: 'u6',
    email: 'cashier@acme.com',
    name: 'Rina Begum',
    primaryRole: 'business_staff',
    primaryScope: 'Acme Main Store',
    status: 'active',
    lastLogin: 'today 09:24',
    createdAt: '2026-02-12',
    mfaEnabled: false,
    emailVerifiedAt: '2026-02-12',
    phoneVerifiedAt: null,
    contactPhone: null,
    language: 'bn',
    memberships: [
      { type: 'business', id: 'biz_300', name: 'Acme Main Store', role: 'staff', joinedAt: '2026-02-12' },
    ],
    recentSessions: [],
  },
  {
    id: 'u7',
    email: 'admin@sundarbantech.com',
    name: 'Mahfuz Rahman',
    primaryRole: 'reseller_admin',
    primaryScope: 'Sundarban Tech',
    status: 'active',
    lastLogin: 'today 08:10',
    createdAt: '2025-11-04',
    mfaEnabled: true,
    emailVerifiedAt: '2025-11-04',
    phoneVerifiedAt: '2025-11-05',
    contactPhone: '+8801XXXXXXX',
    language: 'en',
    memberships: [
      { type: 'reseller', id: 'rsl_2', name: 'Sundarban Tech', role: 'admin', joinedAt: '2025-11-04' },
    ],
    recentSessions: [],
  },
  {
    id: 'u8',
    email: 'nayem@acme-resellers.com',
    name: 'Nayem Hossain',
    primaryRole: 'reseller_admin',
    primaryScope: 'Acme Resellers',
    status: 'invited',
    lastLogin: null,
    createdAt: '2026-04-25',
    mfaEnabled: false,
    emailVerifiedAt: null,
    phoneVerifiedAt: null,
    contactPhone: null,
    language: 'en',
    memberships: [
      { type: 'reseller', id: 'rsl_1', name: 'Acme Resellers', role: 'staff', joinedAt: '2026-04-25' },
    ],
    recentSessions: [],
  },
  {
    id: 'u9',
    email: 'closed@gone.com',
    name: 'Closed Account',
    primaryRole: 'reseller_admin',
    primaryScope: 'Gone Reseller',
    status: 'disabled',
    lastLogin: '12 Mar',
    createdAt: '2025-06-01',
    mfaEnabled: false,
    emailVerifiedAt: '2025-06-01',
    phoneVerifiedAt: null,
    contactPhone: null,
    language: 'en',
    memberships: [
      { type: 'reseller', id: 'rsl_5', name: 'Gone Reseller', role: 'admin', joinedAt: '2025-06-01' },
    ],
    recentSessions: [],
  },
];

/**
 * Cross-business subscription payments (the platform-wide Payments list).
 * Canonical {@link Payment} wire shape — minor units, enum method, ISO paidAt,
 * denormalized business/reseller names. paidAt is null until actually settled
 * (a failed/pending payment never settled).
 */
export const adminPayments: Payment[] = [
  { id: 'p1', invoiceNumber: 'INV-2026-0345', businessId: 'biz_acme_main', businessName: 'Acme Main Store', resellerId: 'rsl_1', resellerName: 'Acme Resellers', status: 'paid', method: 'sslcommerz', amountMinor: 150000, currencyCode: 'BDT', gatewayRef: 'SSL-4c3a', paidAt: '2026-05-03T14:28:00Z' },
  { id: 'p2', invoiceNumber: 'INV-2026-0344', businessId: 'biz_sky_pharmacy', businessName: 'Sky Pharmacy', resellerId: 'rsl_2', resellerName: 'Sundarban Tech', status: 'paid', method: 'sslcommerz', amountMinor: 50000, currencyCode: 'BDT', gatewayRef: 'SSL-8b21', paidAt: '2026-05-03T11:02:00Z' },
  { id: 'p3', invoiceNumber: 'INV-2026-0343', businessId: 'biz_padma_mart', businessName: 'Padma Mart', resellerId: 'rsl_1', resellerName: 'Acme Resellers', status: 'paid', method: 'sslcommerz', amountMinor: 150000, currencyCode: 'BDT', gatewayRef: 'SSL-12d3', paidAt: '2026-05-02T16:45:00Z' },
  { id: 'p4', invoiceNumber: 'INV-2026-0342', businessId: 'biz_greenfields', businessName: 'Greenfields Mart', resellerId: 'rsl_1', resellerName: 'Acme Resellers', status: 'paid', method: 'sslcommerz', amountMinor: 150000, currencyCode: 'BDT', gatewayRef: 'SSL-9aef', paidAt: '2026-05-02T10:15:00Z' },
  { id: 'p5', invoiceNumber: 'INV-2026-0341', businessId: 'biz_khan_bakery', businessName: 'Khan Bakery', resellerId: 'rsl_1', resellerName: 'Acme Resellers', status: 'paid', method: 'manual_cash', amountMinor: 30000, currencyCode: 'BDT', gatewayRef: null, paidAt: '2026-05-02T09:30:00Z' },
  { id: 'p6', invoiceNumber: 'INV-2026-0340', businessId: 'biz_riverside', businessName: 'Riverside Diner', resellerId: 'rsl_2', resellerName: 'Sundarban Tech', status: 'failed', method: 'sslcommerz', amountMinor: 50000, currencyCode: 'BDT', gatewayRef: 'SSL-fail', paidAt: null },
  { id: 'p7', invoiceNumber: 'INV-2026-0339', businessId: 'biz_bookcorner', businessName: 'BookCorner', resellerId: 'rsl_1', resellerName: 'Acme Resellers', status: 'paid', method: 'sslcommerz', amountMinor: 150000, currencyCode: 'BDT', gatewayRef: 'SSL-c1d2', paidAt: '2026-05-01T13:20:00Z' },
  { id: 'p8', invoiceNumber: 'INV-2026-0338', businessId: 'biz_nadia', businessName: 'Nadia Boutique', resellerId: null, resellerName: null, status: 'paid', method: 'bank_transfer', amountMinor: 150000, currencyCode: 'BDT', gatewayRef: null, paidAt: '2026-04-30T15:00:00Z' },
  { id: 'p9', invoiceNumber: 'INV-2026-0337', businessId: 'biz_demo', businessName: 'Demo Store', resellerId: null, resellerName: null, status: 'pending', method: 'sslcommerz', amountMinor: 0, currencyCode: 'BDT', gatewayRef: null, paidAt: null },
  { id: 'p10', invoiceNumber: 'INV-2026-0336', businessId: 'biz_acme_main', businessName: 'Acme Main Store', resellerId: 'rsl_1', resellerName: 'Acme Resellers', status: 'refunded', method: 'sslcommerz', amountMinor: 10000, currencyCode: 'BDT', gatewayRef: 'SSL-rfnd', paidAt: '2026-04-28T12:00:00Z' },
];

/** One business subscribed to a given package (denormalized row for the by-package subscribers view). */
export type PackageSubscriber = {
  id: string;
  business: string;
  reseller: string;
  price: string;
  status: 'active' | 'trial' | 'past_due' | 'canceled';
  renews: string;
};

/** Subscribers to the "Pro" package — drives admin/PackageSubscribers. */
export const packageSubscribers: PackageSubscriber[] = [
  { id: 's1', business: 'Acme Main Store', reseller: 'Acme Resellers', price: 'BDT Monthly', status: 'active', renews: '2026-06-03' },
  { id: 's2', business: 'Sky Pharmacy', reseller: 'Sundarban', price: 'BDT Yearly', status: 'active', renews: '2026-07-30' },
  { id: 's3', business: 'Greenfields Mart', reseller: 'Acme Resellers', price: 'BDT Monthly', status: 'trial', renews: '2026-05-09' },
  { id: 's4', business: 'BookCorner', reseller: 'Acme Resellers', price: 'BDT Monthly', status: 'active', renews: '2026-05-22' },
  { id: 's5', business: 'Padma Mart', reseller: 'Acme Resellers', price: 'BDT Yearly', status: 'active', renews: '2026-08-08' },
  { id: 's6', business: 'Riverside Diner', reseller: 'Sundarban', price: 'BDT Monthly', status: 'past_due', renews: '2026-05-12' },
  { id: 's7', business: 'Nadia Boutique', reseller: 'Direct', price: 'BDT Monthly', status: 'active', renews: '2026-06-15' },
];

/**
 * Reseller-scoped subscriptions, payments, and payouts. Used by ResellerDetail
 * to show "everything related to this reseller's account".
 */
export type ResellerSubscription = {
  id: string;
  businessName: string;
  ownerEmail: string;
  packageName: string;
  amountMinor: number;
  interval: 'month' | 'year';
  status: 'active' | 'trial' | 'past_due' | 'suspended' | 'canceled';
  startedAt: string;
  renewsAt: string;
};

export type ResellerPayment = {
  id: string;
  invoiceNumber: string;
  businessName: string;
  amountMinor: number;
  commissionMinor: number;
  method: 'SSLCommerz' | 'Cash';
  status: 'paid' | 'failed' | 'refunded';
  receivedAt: string;
};

export const resellerSubscriptions: Record<string, ResellerSubscription[]> = {
  rsl_1: [
    { id: 'sub_1', businessName: 'Acme Main Store', ownerEmail: 'sayem@acme.com', packageName: 'Pro · Monthly', amountMinor: 150000, interval: 'month', status: 'active', startedAt: '2026-01-15', renewsAt: '2026-06-03' },
    { id: 'sub_2', businessName: 'Khan Bakery', ownerEmail: 'imran@khan-bakery.com', packageName: 'Starter · Monthly', amountMinor: 30000, interval: 'month', status: 'past_due', startedAt: '2025-04-25', renewsAt: '2026-04-25' },
    { id: 'sub_3', businessName: 'Greenfields Mart', ownerEmail: 'manager@greenfields.bd', packageName: 'Pro · Monthly', amountMinor: 150000, interval: 'month', status: 'trial', startedAt: '2026-05-03', renewsAt: '2026-05-09' },
    { id: 'sub_4', businessName: 'BookCorner', ownerEmail: 'hello@bookcorner.com', packageName: 'Pro · Yearly', amountMinor: 1500000, interval: 'year', status: 'active', startedAt: '2026-02-22', renewsAt: '2027-02-22' },
    { id: 'sub_5', businessName: 'Padma Mart', ownerEmail: 'admin@padmamart.com', packageName: 'Pro · Yearly', amountMinor: 1500000, interval: 'year', status: 'active', startedAt: '2025-08-08', renewsAt: '2026-08-08' },
    { id: 'sub_6', businessName: 'Riverside Diner', ownerEmail: 'owner@riverside.bd', packageName: 'Basic · Monthly', amountMinor: 50000, interval: 'month', status: 'active', startedAt: '2026-04-12', renewsAt: '2026-06-12' },
  ],
  rsl_2: [
    { id: 'sub_7', businessName: 'Sky Pharmacy', ownerEmail: 'sky@sky-pharma.bd', packageName: 'Basic · Monthly', amountMinor: 50000, interval: 'month', status: 'active', startedAt: '2026-04-30', renewsAt: '2026-06-30' },
  ],
  rsl_3: [],
  rsl_4: [],
  rsl_5: [],
};

export const resellerPayments: Record<string, ResellerPayment[]> = {
  rsl_1: [
    { id: 'pay_1', invoiceNumber: 'INV-2026-0345', businessName: 'Acme Main Store', amountMinor: 150000, commissionMinor: 30000, method: 'SSLCommerz', status: 'paid', receivedAt: '2026-05-03' },
    { id: 'pay_2', invoiceNumber: 'INV-2026-0312', businessName: 'Acme Main Store', amountMinor: 150000, commissionMinor: 30000, method: 'SSLCommerz', status: 'paid', receivedAt: '2026-04-03' },
    { id: 'pay_3', invoiceNumber: 'INV-2026-0276', businessName: 'Acme Main Store', amountMinor: 150000, commissionMinor: 30000, method: 'Cash', status: 'paid', receivedAt: '2026-03-03' },
    { id: 'pay_4', invoiceNumber: 'INV-2026-0349', businessName: 'BookCorner', amountMinor: 1500000, commissionMinor: 300000, method: 'SSLCommerz', status: 'paid', receivedAt: '2026-02-22' },
    { id: 'pay_5', invoiceNumber: 'INV-2026-0301', businessName: 'Padma Mart', amountMinor: 1500000, commissionMinor: 300000, method: 'SSLCommerz', status: 'paid', receivedAt: '2025-08-08' },
    { id: 'pay_6', invoiceNumber: 'INV-2026-0298', businessName: 'Riverside Diner', amountMinor: 50000, commissionMinor: 10000, method: 'SSLCommerz', status: 'paid', receivedAt: '2026-04-12' },
    { id: 'pay_7', invoiceNumber: 'INV-2026-0244', businessName: 'Khan Bakery', amountMinor: 30000, commissionMinor: 6000, method: 'Cash', status: 'paid', receivedAt: '2026-03-25' },
    { id: 'pay_8', invoiceNumber: 'INV-2026-0212', businessName: 'Khan Bakery', amountMinor: 30000, commissionMinor: 6000, method: 'SSLCommerz', status: 'failed', receivedAt: '2026-04-25' },
  ],
  rsl_2: [
    { id: 'pay_9', invoiceNumber: 'INV-2026-0349', businessName: 'Sky Pharmacy', amountMinor: 50000, commissionMinor: 7500, method: 'SSLCommerz', status: 'paid', receivedAt: '2026-04-30' },
  ],
  rsl_3: [],
  rsl_4: [],
  rsl_5: [],
};

export type ResellerPayoutDestination =
  | { type: 'bkash'; phone: string; accountHolder: string }
  | { type: 'bank'; bankName: string; accountName: string; accountNumber: string; routingNumber: string; branch: string };

/**
 * A saved payout account on a reseller. Resellers can save multiple
 * (e.g. a primary bank + a backup bKash) — exactly one is the default,
 * which is the destination admins use unless they pick another at transfer time.
 */
export type ResellerPayoutAccount = ResellerPayoutDestination & {
  id: string;
  label: string;
  isDefault: boolean;
  addedAt: string;
  status: 'active' | 'inactive';
};

export type PayoutSchedule = 'biweekly' | 'monthly';

export type ResellerPayout = {
  id: string;
  periodStart: string;
  periodEnd: string;
  amountMinor: number;
  method: 'bank_transfer' | 'bkash' | 'nagad' | 'manual';
  bankRef: string;
  paidAt: string;
  /** Free-text note the admin enters when recording the manual transfer */
  note?: string;
};

/**
 * Reseller-owned packages. Every reseller defines their own catalog with
 * their own pricing — there is no shared "platform package" customers buy
 * directly. Customer always buys a reseller's package; platform takes a
 * variable commission cut (set per-reseller by the platform admin).
 */
/* ── Platform pricing tiers (price-floor registry / MAP policy) ─────────────
 * ITCity-controlled registry that defines a minimum monthly + yearly price
 * for every reseller-owned package. See SAAS_REARCHITECTURE_PLAN.md
 * Decision #20 and BILLING_FLOW.md "Reseller price floors".
 */

export type PlatformPricingTierSlug =
  | 'tier_starter'
  | 'tier_basic'
  | 'tier_pro'
  | 'tier_enterprise';

export type PlatformPricingTier = {
  id: string;
  slug: PlatformPricingTierSlug;
  displayName: string;
  description: string;
  defaultMinMonthlyMinor: number;
  defaultMinYearlyMinor: number;
  hintMaxLocations: number | null;
  hintMaxUsers: number | null;
  hintMaxProducts: number | null;
  isActive: boolean;
  sortOrder: number;
  packageCount: number; // resolved at render time; baked here for the mock
};

export const platformPricingTiers: PlatformPricingTier[] = [
  {
    id: 'pt_starter',
    slug: 'tier_starter',
    displayName: 'Starter',
    description: 'Single location, very small business',
    defaultMinMonthlyMinor: 25000,
    defaultMinYearlyMinor: 300000,
    hintMaxLocations: 1,
    hintMaxUsers: 2,
    hintMaxProducts: 1000,
    isActive: true,
    sortOrder: 10,
    packageCount: 4,
  },
  {
    id: 'pt_basic',
    slug: 'tier_basic',
    displayName: 'Basic',
    description: 'Small-to-mid shop, 2-5 staff',
    defaultMinMonthlyMinor: 40000,
    defaultMinYearlyMinor: 480000,
    hintMaxLocations: 2,
    hintMaxUsers: 5,
    hintMaxProducts: 2000,
    isActive: true,
    sortOrder: 20,
    packageCount: 5,
  },
  {
    id: 'pt_pro',
    slug: 'tier_pro',
    displayName: 'Pro',
    description: 'Multi-location, growing chain',
    defaultMinMonthlyMinor: 120000,
    defaultMinYearlyMinor: 1440000,
    hintMaxLocations: 5,
    hintMaxUsers: 15,
    hintMaxProducts: 10000,
    isActive: true,
    sortOrder: 30,
    packageCount: 4,
  },
  {
    id: 'pt_enterprise',
    slug: 'tier_enterprise',
    displayName: 'Enterprise',
    description: 'Large chain, unlimited resources',
    defaultMinMonthlyMinor: 450000,
    defaultMinYearlyMinor: 5400000,
    hintMaxLocations: null,
    hintMaxUsers: null,
    hintMaxProducts: null,
    isActive: true,
    sortOrder: 40,
    packageCount: 1,
  },
];

/** Global absolute floor — even with overrides, no package can go below this. */
export const GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT = 200;
export const GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT = 2000;

/**
 * Per-reseller price floor overrides.
 * Shape: { resellerId: { tierSlug: { minMonthly, minYearly } } }.
 * Tiers not present in this map fall back to the platform tier's defaults.
 */
export type ResellerFloorOverride = {
  minMonthlyMinor: number;
  minYearlyMinor: number;
  setByEmail: string;
  setAt: string;
  note?: string;
};

export const resellerFloorOverrides: Record<
  string,
  Partial<Record<PlatformPricingTierSlug, ResellerFloorOverride>>
> = {
  rsl_1: {
    tier_pro: {
      minMonthlyMinor: 110000,
      minYearlyMinor: 1320000,
      setByEmail: 'sayem@itcitysoft.com',
      setAt: '2026-04-12',
      note: 'High-volume partner — agreed at quarterly review',
    },
  },
  rsl_2: {},
  rsl_3: {},
  rsl_4: {},
  rsl_5: {},
};

/** Resolve effective floor for a reseller × tier (server-side validation mirror). */
export function resolveFloor(
  resellerId: string,
  tierSlug: PlatformPricingTierSlug,
): { monthly: number; yearly: number; source: 'override' | 'tier_default' | 'global_absolute' } {
  const tier = platformPricingTiers.find((t) => t.slug === tierSlug);
  const override = resellerFloorOverrides[resellerId]?.[tierSlug];
  const monthlyCandidates = [
    GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT,
    tier?.defaultMinMonthlyMinor ?? 0,
    override?.minMonthlyMinor ?? 0,
  ];
  const yearlyCandidates = [
    GLOBAL_ABSOLUTE_FLOOR_YEARLY_BDT,
    tier?.defaultMinYearlyMinor ?? 0,
    override?.minYearlyMinor ?? 0,
  ];
  const monthly = Math.max(...monthlyCandidates);
  const yearly = Math.max(...yearlyCandidates);
  const source: 'override' | 'tier_default' | 'global_absolute' =
    override && override.minMonthlyMinor === monthly
      ? 'override'
      : monthly === GLOBAL_ABSOLUTE_FLOOR_MONTHLY_BDT
        ? 'global_absolute'
        : 'tier_default';
  return { monthly, yearly, source };
}

export type ResellerPackage = {
  id: string;
  resellerId: string;
  internalCode: string;
  name: string;
  tagline: string;
  /** Tier this package is mapped to — drives the price floor. */
  tierSlug: PlatformPricingTierSlug;
  monthlyMinor: number;
  yearlyMinor: number;
  status: 'draft' | 'published' | 'deprecated' | 'archived';
  subscriberCount: number;
  features: {
    locations: number | 'unlimited';
    users: number | 'unlimited';
    products: number | 'unlimited';
    invoices: number | 'unlimited';
    modules: string[];
  };
  createdAt: string;
};

const STD_MODULES = ['CRM', 'Essentials', 'Manufacturing', 'RMA', 'Service', 'Accounts', 'E-commerce'];

export const resellerPackages: Record<string, ResellerPackage[]> = {
  rsl_1: [
    {
      id: 'rpk_1_starter',
      resellerId: 'rsl_1',
      internalCode: 'acme-starter',
      name: 'Acme Starter',
      tagline: 'Single shop, neighbourhood pricing',
      tierSlug: 'tier_starter',
      monthlyMinor: 35000,
      yearlyMinor: 420000,
      status: 'published',
      subscriberCount: 64,
      features: { locations: 1, users: 3, products: 1500, invoices: 1500, modules: STD_MODULES },
      createdAt: '2026-01-15',
    },
    {
      id: 'rpk_1_growth',
      resellerId: 'rsl_1',
      internalCode: 'acme-growth',
      name: 'Acme Growth',
      tagline: 'Most popular · 2-3 outlets — grandfathered below current Pro floor',
      tierSlug: 'tier_pro',
      monthlyMinor: 75000,
      yearlyMinor: 900000,
      status: 'published',
      subscriberCount: 48,
      features: { locations: 3, users: 12, products: 5000, invoices: 5000, modules: STD_MODULES },
      createdAt: '2026-01-15',
    },
    {
      id: 'rpk_1_pro',
      resellerId: 'rsl_1',
      internalCode: 'acme-pro',
      name: 'Acme Pro',
      tagline: 'Multi-location chains',
      tierSlug: 'tier_pro',
      monthlyMinor: 180000,
      yearlyMinor: 2160000,
      status: 'published',
      subscriberCount: 16,
      features: { locations: 10, users: 30, products: 'unlimited', invoices: 'unlimited', modules: STD_MODULES },
      createdAt: '2026-02-01',
    },
    {
      id: 'rpk_1_eid',
      resellerId: 'rsl_1',
      internalCode: 'acme-eid-special',
      name: 'Eid Special',
      tagline: 'Limited-time bundle',
      tierSlug: 'tier_basic',
      monthlyMinor: 50000,
      yearlyMinor: 600000,
      status: 'draft',
      subscriberCount: 0,
      features: { locations: 2, users: 6, products: 2500, invoices: 2500, modules: STD_MODULES },
      createdAt: '2026-04-22',
    },
  ],
  rsl_2: [
    {
      id: 'rpk_2_basic',
      resellerId: 'rsl_2',
      internalCode: 'sundarban-basic',
      name: 'Sundarban Basic',
      tagline: 'Affordable starter for Khulna shops',
      tierSlug: 'tier_basic',
      monthlyMinor: 40000,
      yearlyMinor: 480000,
      status: 'published',
      subscriberCount: 45,
      features: { locations: 1, users: 4, products: 2000, invoices: 2000, modules: STD_MODULES },
      createdAt: '2025-11-10',
    },
    {
      id: 'rpk_2_premium',
      resellerId: 'rsl_2',
      internalCode: 'sundarban-premium',
      name: 'Sundarban Premium',
      tagline: 'Restaurant + retail combo',
      tierSlug: 'tier_pro',
      monthlyMinor: 120000,
      yearlyMinor: 1440000,
      status: 'published',
      subscriberCount: 28,
      features: { locations: 5, users: 15, products: 'unlimited', invoices: 'unlimited', modules: STD_MODULES },
      createdAt: '2025-11-10',
    },
  ],
  rsl_3: [
    {
      id: 'rpk_3_solo',
      resellerId: 'rsl_3',
      internalCode: 'citypos-solo',
      name: 'CityPOS Solo',
      tagline: 'For Dhaka boutiques',
      tierSlug: 'tier_basic',
      monthlyMinor: 60000,
      yearlyMinor: 720000,
      status: 'published',
      subscriberCount: 38,
      features: { locations: 1, users: 5, products: 3000, invoices: 3000, modules: STD_MODULES },
      createdAt: '2025-10-01',
    },
    {
      id: 'rpk_3_chain',
      resellerId: 'rsl_3',
      internalCode: 'citypos-chain',
      name: 'CityPOS Chain',
      tagline: 'Multi-store retail',
      tierSlug: 'tier_pro',
      monthlyMinor: 200000,
      yearlyMinor: 2400000,
      status: 'published',
      subscriberCount: 13,
      features: { locations: 8, users: 25, products: 'unlimited', invoices: 'unlimited', modules: STD_MODULES },
      createdAt: '2025-10-01',
    },
  ],
  rsl_4: [],
  rsl_5: [],
};

/**
 * Reseller payout config — how + when each reseller wants to be paid.
 * Each reseller can save MULTIPLE accounts (mix of bKash + bank); one is
 * marked default. Edited from /reseller/settings → Payout details tab.
 */
export const resellerPayoutConfig: Record<string, {
  schedule: PayoutSchedule;
  accounts: ResellerPayoutAccount[];
  minPayoutMinor: number;
}> = {
  rsl_1: {
    schedule: 'monthly',
    minPayoutMinor: 100000,
    accounts: [
      {
        id: 'pa_1_1',
        label: 'EBL — primary',
        isDefault: true,
        addedAt: '2026-01-12',
        status: 'active',
        type: 'bank',
        bankName: 'Eastern Bank Limited',
        accountName: 'Acme Resellers Ltd.',
        accountNumber: '1141060123456',
        routingNumber: '095261734',
        branch: 'Sylhet Main Branch',
      },
      {
        id: 'pa_1_2',
        label: 'bKash — backup',
        isDefault: false,
        addedAt: '2026-02-04',
        status: 'active',
        type: 'bkash',
        phone: '+8801712345678',
        accountHolder: 'Rakib Ahmed',
      },
      {
        id: 'pa_1_3',
        label: 'Brac Bank — owner personal',
        isDefault: false,
        addedAt: '2026-03-15',
        status: 'active',
        type: 'bank',
        bankName: 'BRAC Bank',
        accountName: 'Rakib Ahmed',
        accountNumber: '2050778899012',
        routingNumber: '060274856',
        branch: 'Sylhet Zindabazar',
      },
    ],
  },
  rsl_2: {
    schedule: 'biweekly',
    minPayoutMinor: 50000,
    accounts: [
      {
        id: 'pa_2_1',
        label: 'bKash — primary',
        isDefault: true,
        addedAt: '2025-11-04',
        status: 'active',
        type: 'bkash',
        phone: '+8801712345678',
        accountHolder: 'Mahfuz Rahman',
      },
      {
        id: 'pa_2_2',
        label: 'DBBL — secondary',
        isDefault: false,
        addedAt: '2026-01-22',
        status: 'active',
        type: 'bank',
        bankName: 'Dutch-Bangla Bank',
        accountName: 'Sundarban Tech',
        accountNumber: '1011890034567',
        routingNumber: '090272345',
        branch: 'Mirpur',
      },
    ],
  },
  rsl_3: {
    schedule: 'monthly',
    minPayoutMinor: 100000,
    accounts: [
      {
        id: 'pa_3_1',
        label: 'BRAC Bank — primary',
        isDefault: true,
        addedAt: '2025-09-22',
        status: 'active',
        type: 'bank',
        bankName: 'BRAC Bank',
        accountName: 'CityPOS Bangla',
        accountNumber: '2050332456789',
        routingNumber: '060274856',
        branch: 'Dhanmondi',
      },
    ],
  },
  rsl_4: {
    schedule: 'monthly',
    minPayoutMinor: 100000,
    accounts: [
      {
        id: 'pa_4_1',
        label: 'bKash — primary',
        isDefault: true,
        addedAt: '2026-04-25',
        status: 'active',
        type: 'bkash',
        phone: '+8801911223344',
        accountHolder: 'Tanvir Ahmed',
      },
    ],
  },
  rsl_5: {
    schedule: 'monthly',
    minPayoutMinor: 100000,
    accounts: [
      {
        id: 'pa_5_1',
        label: 'bKash',
        isDefault: true,
        addedAt: '2025-06-01',
        status: 'inactive',
        type: 'bkash',
        phone: '+8801555667788',
        accountHolder: 'Closed Account',
      },
    ],
  },
};

/* ── Reseller applications ─────────────────────────────────────────────────
 * Public submissions to /become-a-reseller. The platform admin reviews each
 * one; on approval the system creates a `resellers` row + `reseller_users`
 * pivot owner row using the applicant's existing user_id.
 *
 * Status machine:
 *   pending           → first state after submission
 *   in_review         → admin opened it (optional ack)
 *   more_info_needed  → admin asked for additional info; applicant must reply
 *   approved          → reseller row created; invite (or "finish setup") sent
 *   rejected          → admin declined; cooldown counter starts
 *   withdrawn         → applicant pulled the application before review
 *
 * Linkage to the rest of the system:
 *   - applicantUserId may be NULL for anonymous applications (a user row is
 *     pre-created in `pending` status)
 *   - resellerId is set only after approval (points at the resulting row)
 */

export type ResellerApplicationStatus =
  | 'pending'
  | 'in_review'
  | 'more_info_needed'
  | 'approved'
  | 'rejected'
  | 'withdrawn';

export type ResellerApplication = {
  id: string;
  applicantUserId: string | null;
  applicantName: string;
  email: string;
  phone: string;
  country: string;
  orgName: string;
  orgWebsite?: string;
  expectedVolumeMinor: number;
  yearsInBusiness: number;
  whyUs: string;
  references?: string;
  submittedAt: string;
  status: ResellerApplicationStatus;
  reviewedBy?: string;
  reviewedAt?: string;
  decisionReason?: string;
  internalNotes?: string;
  resellerId?: string;
  reapplyAfter?: string;
  // History of admin notes / reseller replies
  timeline: Array<{
    at: string;
    by: string;
    kind: 'submitted' | 'note' | 'status_change' | 'reply';
    text: string;
  }>;
};

export const resellerApplications: ResellerApplication[] = [
  {
    id: 'app_1',
    applicantUserId: 'u_app_1',
    applicantName: 'Farzana Khatun',
    email: 'farzana@dhaka-pos-pros.com',
    phone: '+8801711000111',
    country: 'BD',
    orgName: 'Dhaka POS Pros',
    orgWebsite: 'https://dhakaposperos.com',
    expectedVolumeMinor: 25000000,
    yearsInBusiness: 4,
    whyUs:
      'We already onboard small retailers to existing POS tools and manage their day-to-day. Want a Bangla-first product with local payment rails so we can replace what we resell now.',
    references: '3 existing customers happy to vouch — list available on request.',
    submittedAt: '2026-05-02T11:24:00Z',
    status: 'pending',
    timeline: [
      { at: '2026-05-02T11:24:00Z', by: 'Farzana Khatun', kind: 'submitted', text: 'Application submitted.' },
    ],
  },
  {
    id: 'app_2',
    applicantUserId: 'u_app_2',
    applicantName: 'Sumon Bhuiyan',
    email: 'sumon@khulna-it.com',
    phone: '+8801812000222',
    country: 'BD',
    orgName: 'Khulna IT Services',
    expectedVolumeMinor: 8000000,
    yearsInBusiness: 2,
    whyUs:
      'Local IT shop in Khulna serving 30+ businesses. Want to expand into POS resale.',
    submittedAt: '2026-05-01T08:10:00Z',
    status: 'in_review',
    reviewedBy: 'admin@itcitysoft.com',
    reviewedAt: '2026-05-02T09:01:00Z',
    timeline: [
      { at: '2026-05-01T08:10:00Z', by: 'Sumon Bhuiyan', kind: 'submitted', text: 'Application submitted.' },
      { at: '2026-05-02T09:01:00Z', by: 'admin@itcitysoft.com', kind: 'status_change', text: 'Marked as in_review.' },
    ],
  },
  {
    id: 'app_3',
    applicantUserId: 'u_app_3',
    applicantName: 'Imran Hossain',
    email: 'imran@bd-pos-bazaar.com',
    phone: '+8801913000333',
    country: 'BD',
    orgName: 'BD POS Bazaar',
    orgWebsite: 'https://bdposbazaar.com',
    expectedVolumeMinor: 60000000,
    yearsInBusiness: 6,
    whyUs:
      'We already manage SaaS subscriptions for 200+ SMBs. POS Signin would be a strong addition to our portfolio.',
    references: 'Current vendors: Surecart, Tally Bangladesh.',
    submittedAt: '2026-04-29T13:45:00Z',
    status: 'more_info_needed',
    reviewedBy: 'admin@itcitysoft.com',
    reviewedAt: '2026-04-30T10:11:00Z',
    decisionReason:
      'Need: company registration number, current portfolio size, signed letter from at least one customer.',
    timeline: [
      { at: '2026-04-29T13:45:00Z', by: 'Imran Hossain', kind: 'submitted', text: 'Application submitted.' },
      { at: '2026-04-30T10:11:00Z', by: 'admin@itcitysoft.com', kind: 'note', text: 'Need company registration + customer reference letter.' },
      { at: '2026-04-30T10:11:00Z', by: 'admin@itcitysoft.com', kind: 'status_change', text: 'Status set to more_info_needed.' },
    ],
  },
  {
    id: 'app_4',
    applicantUserId: 'u_4',
    applicantName: 'Tanvir Ahmed',
    email: 'hello@beta-res.com',
    phone: '+8801XXXXXXX',
    country: 'BD',
    orgName: 'Beta Resellers',
    expectedVolumeMinor: 12000000,
    yearsInBusiness: 3,
    whyUs:
      'Family-run IT business with strong relationships across Sylhet and Sunamganj. Want to focus on retail POS resale.',
    submittedAt: '2026-04-25T07:00:00Z',
    status: 'approved',
    reviewedBy: 'admin@itcitysoft.com',
    reviewedAt: '2026-04-26T11:30:00Z',
    decisionReason: 'Strong references and clear plan. Initial commission 15% (revisit at 60 days).',
    resellerId: 'rsl_4',
    timeline: [
      { at: '2026-04-25T07:00:00Z', by: 'Tanvir Ahmed', kind: 'submitted', text: 'Application submitted.' },
      { at: '2026-04-26T11:30:00Z', by: 'admin@itcitysoft.com', kind: 'note', text: 'Strong references; approving at 15% commission.' },
      { at: '2026-04-26T11:30:00Z', by: 'admin@itcitysoft.com', kind: 'status_change', text: 'Approved. Reseller rsl_4 created. Invite sent.' },
    ],
  },
  {
    id: 'app_5',
    applicantUserId: null,
    applicantName: 'Sajid Mahmud',
    email: 'sajid@spam-trial.com',
    phone: '+99999999999',
    country: 'XX',
    orgName: 'Whatever Reselling',
    expectedVolumeMinor: 0,
    yearsInBusiness: 0,
    whyUs: 'lol just trying',
    submittedAt: '2026-04-20T03:14:00Z',
    status: 'rejected',
    reviewedBy: 'admin@itcitysoft.com',
    reviewedAt: '2026-04-20T09:00:00Z',
    decisionReason: 'Insufficient information / suspected non-serious application.',
    reapplyAfter: '2026-05-20',
    timeline: [
      { at: '2026-04-20T03:14:00Z', by: 'anonymous', kind: 'submitted', text: 'Application submitted.' },
      { at: '2026-04-20T09:00:00Z', by: 'admin@itcitysoft.com', kind: 'status_change', text: 'Rejected. Reapply window: 30 days.' },
    ],
  },
  {
    id: 'app_6',
    applicantUserId: 'u_app_6',
    applicantName: 'Rina Begum',
    email: 'rina@retail-bd.com',
    phone: '+8801555000666',
    country: 'BD',
    orgName: 'Retail BD',
    expectedVolumeMinor: 9000000,
    yearsInBusiness: 1,
    whyUs: 'Just starting — eager to learn the SaaS resale model.',
    submittedAt: '2026-04-18T15:00:00Z',
    status: 'withdrawn',
    timeline: [
      { at: '2026-04-18T15:00:00Z', by: 'Rina Begum', kind: 'submitted', text: 'Application submitted.' },
      { at: '2026-04-19T08:00:00Z', by: 'Rina Begum', kind: 'status_change', text: 'Withdrew application — applying again later this year.' },
    ],
  },
  {
    id: 'app_7',
    applicantUserId: 'u_app_7',
    applicantName: 'Mokter Hossain',
    email: 'mokter@chittagong-tech.com',
    phone: '+8801611000777',
    country: 'BD',
    orgName: 'Chittagong Tech',
    expectedVolumeMinor: 17500000,
    yearsInBusiness: 5,
    whyUs:
      'Currently a Possignin direct customer (Chittagong Restaurants Co.). Want to start reselling to my supplier network.',
    submittedAt: '2026-05-03T05:30:00Z',
    status: 'pending',
    internalNotes: 'Existing direct customer — verify there is no conflict of interest with current account.',
    timeline: [
      { at: '2026-05-03T05:30:00Z', by: 'Mokter Hossain', kind: 'submitted', text: 'Application submitted (already a direct customer).' },
    ],
  },
];

/** Helper: count of apps awaiting admin action (used for sidebar badge). */
export const pendingApplicationsCount = resellerApplications.filter(
  (a) => a.status === 'pending' || a.status === 'in_review' || a.status === 'more_info_needed',
).length;

export const resellerPayouts: Record<string, ResellerPayout[]> = {
  rsl_1: [
    { id: 'po_1', periodStart: '2026-04-01', periodEnd: '2026-04-30', amountMinor: 3820000, method: 'bank_transfer', bankRef: 'XYZ123', paidAt: '2026-05-15' },
    { id: 'po_2', periodStart: '2026-03-01', periodEnd: '2026-03-31', amountMinor: 4180000, method: 'bank_transfer', bankRef: 'XYZ045', paidAt: '2026-04-15' },
    { id: 'po_3', periodStart: '2026-02-01', periodEnd: '2026-02-28', amountMinor: 3960000, method: 'bank_transfer', bankRef: 'XYZ002', paidAt: '2026-03-15' },
    { id: 'po_4', periodStart: '2026-01-01', periodEnd: '2026-01-31', amountMinor: 3540000, method: 'bkash', bankRef: 'BK-9921', paidAt: '2026-02-15' },
  ],
  rsl_2: [
    { id: 'po_5', periodStart: '2026-04-01', periodEnd: '2026-04-30', amountMinor: 1230000, method: 'bank_transfer', bankRef: 'SUN-001', paidAt: '2026-05-15' },
  ],
  rsl_3: [],
  rsl_4: [],
  rsl_5: [],
};
